/**
 * SSE Job Monitoring Hook
 * 
 * Provides real-time job monitoring using Server-Sent Events (SSE)
 * for more efficient and responsive progress updates.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { config } from '../config';
import { apiCall } from '../services/api.service';
import { emitAuthFailure } from '../services/authEvents';

export const useSSEJobMonitoring = () => {
  const [activeJobs, setActiveJobs] = useState(new Map());
  const eventSourcesRef = useRef(new Map());
  const callbacksRef = useRef(new Map());
  const reconnectTimeoutsRef = useRef(new Map());
  const fullProgressTimeoutsRef = useRef(new Map()); // Track when jobs reach 100% progress
  // Terminal state belongs to the JOB, not to a connection. It used to be a
  // `let` inside connectSSE, so every reconnect started again believing the job
  // was unfinished — a job that had already reported "completed" was treated as
  // live on the very next attempt, which is how a finished job kept a stream
  // reopening for eight minutes (local, 2026-08-09 22:57 → 23:05).
  const completedJobsRef = useRef(new Set());
  // "We already fired onComplete and closed" — separate from the fact above,
  // because two different branches can detect the end (a terminal status, or a
  // job_completed/stream_end event) and only one of them may act on it.
  const completionNotifiedRef = useRef(new Set());

  const startSSEMonitoring = useCallback((jobId, onUpdate = null, onComplete = null) => {
    console.log(`🌊 Starting SSE monitoring for job: ${jobId}`);

    // A deliberate new start on the same id is a new run — forget the previous
    // one's terminal state. Only an explicit start clears these; they outlive
    // stopSSEMonitoring on purpose, so a late `onerror` from a stream we just
    // closed still knows the job was finished and stays quiet.
    completedJobsRef.current.delete(jobId);
    completionNotifiedRef.current.delete(jobId);
    if (completedJobsRef.current.size > 200) completedJobsRef.current.clear();
    if (completionNotifiedRef.current.size > 200) completionNotifiedRef.current.clear();

    // Register callbacks
    if (onUpdate || onComplete) {
      callbacksRef.current.set(jobId, { onUpdate, onComplete });
    }
    
    // Initialize job state
    setActiveJobs(prev => new Map(prev.set(jobId, {
      jobId,
      status: 'connecting',
      progress: 0,
      step: 'initializing',
      message: 'Connecting to job stream...',
      version: 'v5_sse',
      isSSE: true,
      connectionAttempts: 0,
      lastUpdate: new Date().toISOString()
    })));

    const connectSSE = async (attemptNumber = 0) => {
      // Close existing connection if any
      const existingES = eventSourcesRef.current.get(jobId);
      if (existingES) {
        existingES.close();
      }

      // Fetch a short-lived SSE token so the backend can identify the caller
      // and emit `session_expired` if the session was explicitly revoked.
      let sseTokenParam = '';
      try {
        const tokenRes = await apiCall('/api/v1/session/sse-token', { method: 'POST' });
        if (tokenRes?.token) {
          sseTokenParam = `?sseToken=${encodeURIComponent(tokenRes.token)}`;
        }
      } catch {
        // Non-fatal: stream still works without the token (no revocation check)
      }

      const eventSourceUrl = `${config.apiUrl}/api/jobs/v5/stream/${jobId}${sseTokenParam}`;
      console.log(`🌊 Connecting SSE to: ${eventSourceUrl} (attempt ${attemptNumber + 1})`);

      const eventSource = new EventSource(eventSourceUrl);
      eventSourcesRef.current.set(jobId, eventSource);
      const isJobCompleted = () => completedJobsRef.current.has(jobId);

      eventSource.addEventListener('session_expired', () => {
        console.warn(`🔐 SSE session_expired for job ${jobId} — triggering auth recovery`);
        eventSource.close();
        eventSourcesRef.current.delete(jobId);
        emitAuthFailure('jobs_v5_sse_session_expired');
      });

      // Update connection attempt
      setActiveJobs(prev => {
        const updated = new Map(prev);
        const job = updated.get(jobId) || {};
        updated.set(jobId, {
          ...job,
          connectionAttempts: attemptNumber + 1,
          connectionStatus: 'connecting',
          lastConnectionAttempt: new Date().toISOString()
        });
        return updated;
      });

      eventSource.onopen = () => {
        console.log(`🌊 SSE connection established for job: ${jobId}`);
        
        setActiveJobs(prev => {
          const updated = new Map(prev);
          const job = updated.get(jobId) || {};
          updated.set(jobId, {
            ...job,
            connectionStatus: 'connected',
            connectedAt: new Date().toISOString()
          });
          return updated;
        });
      };

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          // Dev only, and it is not about noise — it is about memory.
          //
          // A console entry logged with an OBJECT keeps a strong reference to
          // it for the life of the page: the browser cannot collect what the
          // console can still expand. One per message is fine for a job that
          // takes a minute and fatal for one that takes hours.
          //
          // 2026-09-08: a tab watching a SAP harvest reached ~86,000 console
          // entries and Safari reloaded the page for memory. The reloaded tab
          // could not open new connections, every request failed, and it read
          // exactly like a backend outage — the service was answering 200 to
          // every other client the whole time. Half a night went into proving
          // that from the wrong end.
          //
          // The volume came from a server-side latch that pushed one identical
          // event per second (fixed alongside this), but the retention is this
          // line's doing, and either one alone would still be a slow leak.
          if (import.meta.env.DEV) {
            console.log(`🌊 SSE message received for job ${jobId}:`, {
              type: data.type,
              progress: data.data?.progress,
              status: data.data?.status,
              step: data.data?.step
            });
          }

          // Mark terminal HERE, synchronously, before touching state. It used
          // to be set inside the setActiveJobs updater, which React runs when
          // it pleases — and the server closes 0.2s after the final event, so
          // `onerror` fired first and read the flag as still false. The guard
          // existed and lost the race on every single completion.
          const reachedTerminal =
            data.type === 'job_completed' ||
            data.stream_end === true ||
            ['completed', 'failed', 'cancelled'].includes(data.data?.status);
          if (reachedTerminal) {
            completedJobsRef.current.add(jobId);
          }

          // Update job state
          setActiveJobs(prev => {
            const updated = new Map(prev);
            const currentJob = updated.get(jobId) || {};
            
            // If this is a completion event, ensure status is set correctly
            const isCompletionEvent = data.type === 'job_completed' || data.stream_end;
            
            // Extract progress, status, step, message from data.data
            const progress = data.data?.progress ?? currentJob.progress ?? 0;
            const status = isCompletionEvent 
              ? (data.data?.status || 'completed') 
              : (data.data?.status || currentJob.status || 'running');
            const step = data.data?.step ?? currentJob.step;
            const message = data.data?.message ?? currentJob.message;
            
            const updatedJob = {
              ...currentJob,
              ...data.data,  // Spread all data from backend
              progress: typeof progress === 'number' ? progress : parseFloat(progress) || 0,
              status: status,
              step: step,
              message: message,
              lastUpdate: new Date().toISOString(),
              connectionStatus: 'connected',
              messageType: data.type,
              bridgeData: data.bridge_data,
              changes: data.changes,
              streamInfo: data.stream_info,
              streamEnd: data.stream_end
            };

            updated.set(jobId, updatedJob);

            // Call update callback
            const callbacks = callbacksRef.current.get(jobId);
            if (callbacks?.onUpdate) {
              callbacks.onUpdate(updatedJob);
            }

            // Check if job reached terminal state
            if (updatedJob.status === 'completed' || updatedJob.status === 'failed' || updatedJob.status === 'cancelled') {
              console.log(`🏁 SSE job completed (detected via status): ${jobId}, status: ${updatedJob.status}`);
              completedJobsRef.current.add(jobId);
              completionNotifiedRef.current.add(jobId);

              // Clear any full progress timeout
              const fullProgressTimeout = fullProgressTimeoutsRef.current.get(jobId);
              if (fullProgressTimeout) {
                clearTimeout(fullProgressTimeout);
                fullProgressTimeoutsRef.current.delete(jobId);
              }

              const callbacks = callbacksRef.current.get(jobId);
              if (callbacks?.onComplete) {
                callbacks.onComplete(updatedJob);
              }

              // Close NOW. The 2s grace this replaces was a window the browser
              // used to reconnect on its own: the server had already closed at
              // ~0.2s, and EventSource retries a closed stream by default.
              stopSSEMonitoring(jobId);
            }
            
            // Timeout: If progress is 100% but status is still "running", close stream after 180 seconds (3 minutes)
            // This prevents EventSource from staying open indefinitely if backend doesn't close the stream
            // Extended timeout to allow for Postgres index updates which can take significant time
            if (updatedJob.progress >= 100 && updatedJob.status === 'running') {
              // Clear existing timeout if any
              const existingTimeout = fullProgressTimeoutsRef.current.get(jobId);
              if (existingTimeout) {
                clearTimeout(existingTimeout);
              }
              
              // Set new timeout to close stream after 180 seconds (3 minutes)
              const timeoutId = setTimeout(() => {
                console.warn(`⚠️ Job ${jobId} has been at 100% progress for 180s but status is still "running". Closing EventSource.`);
                
                // Force completion callback
                const callbacks = callbacksRef.current.get(jobId);
                if (callbacks?.onComplete) {
                  callbacks.onComplete({
                    ...updatedJob,
                    status: 'completed',
                    message: 'Job completed (timeout closed)'
                  });
                }
                
                // Close connection
                stopSSEMonitoring(jobId);
              }, 180000); // 180 seconds (3 minutes) timeout - allows time for Postgres index updates
              
              fullProgressTimeoutsRef.current.set(jobId, timeoutId);
            } else if (updatedJob.progress < 100 || updatedJob.status !== 'running') {
              // Clear timeout if progress dropped or status changed
              const fullProgressTimeout = fullProgressTimeoutsRef.current.get(jobId);
              if (fullProgressTimeout) {
                clearTimeout(fullProgressTimeout);
                fullProgressTimeoutsRef.current.delete(jobId);
              }
            }

            return updated;
          });

          // Handle completion via event type (only if not already handled above)
          if (!completionNotifiedRef.current.has(jobId) && (data.type === 'job_completed' || data.stream_end)) {
            console.log(`🏁 SSE job completed (via event type): ${jobId}`);
            completionNotifiedRef.current.add(jobId);

            // Update job state to completed before calling callback
            setActiveJobs(prev => {
              const updated = new Map(prev);
              const currentJob = updated.get(jobId) || {};
              const completedJob = {
                ...currentJob,
                ...data.data,
                status: data.data?.status || 'completed',
                progress: data.data?.progress ?? 100,
                lastUpdate: new Date().toISOString(),
                messageType: data.type,
                streamEnd: data.stream_end
              };
              updated.set(jobId, completedJob);
              
              // Call update callback first to update UI
              const callbacks = callbacksRef.current.get(jobId);
              if (callbacks?.onUpdate) {
                callbacks.onUpdate(completedJob);
              }
              
              // Then call completion callback
              if (callbacks?.onComplete) {
                callbacks.onComplete(completedJob);
              }
              
              return updated;
            });

            // Same reason as above: closing later means the browser reconnects
            // first, and the server answers a finished job all over again.
            stopSSEMonitoring(jobId);
          }

        } catch (error) {
          console.error(`❌ Error parsing SSE message for job ${jobId}:`, error);
        }
      };

      eventSource.onerror = async (error) => {
        // Stream closed after completion event — expected, don't probe or retry
        if (isJobCompleted()) {
          console.log(`✅ SSE stream closed after completion for job ${jobId} — no retry needed`);
          const es = eventSourcesRef.current.get(jobId);
          if (es) { es.close(); eventSourcesRef.current.delete(jobId); }
          return;
        }

        console.error(`❌ SSE error for job ${jobId}:`, error);

        // EventSource doesn't expose HTTP status. On first failure, do a quick
        // REST probe: if the job no longer exists (404) skip all retries.
        if (attemptNumber === 0) {
          try {
            const probeUrl = eventSourceUrl.replace('/stream/', '/status/').split('?')[0];
            const probe = await fetch(probeUrl, { method: 'GET' });
            if (probe.status === 404) {
              console.warn(`🛑 Job ${jobId} not found (404) — stopping SSE monitoring`);
              const es = eventSourcesRef.current.get(jobId);
              if (es) { es.close(); eventSourcesRef.current.delete(jobId); }
              setActiveJobs(prev => {
                const updated = new Map(prev);
                const job = updated.get(jobId) || {};
                updated.set(jobId, {
                  ...job,
                  status: 'failed',
                  step: 'not_found',
                  connectionStatus: 'not_found',
                  message: 'Job not found — it may have expired or the server restarted.',
                  lastUpdate: new Date().toISOString(),
                });
                return updated;
              });
              return;
            }
          } catch { /* probe failed — continue with normal retry */ }
        }
        
        setActiveJobs(prev => {
          const updated = new Map(prev);
          const job = updated.get(jobId) || {};
          updated.set(jobId, {
            ...job,
            connectionStatus: 'error',
            lastError: new Date().toISOString(),
            errorCount: (job.errorCount || 0) + 1
          });
          return updated;
        });

        // Implement exponential backoff for reconnection
        const maxAttempts = 5;
        if (attemptNumber < maxAttempts) {
          const backoffDelay = Math.min(1000 * Math.pow(2, attemptNumber), 30000);
          console.log(`🔄 SSE reconnecting for job ${jobId} in ${backoffDelay}ms...`);
          
          const timeoutId = setTimeout(() => {
            connectSSE(attemptNumber + 1);
          }, backoffDelay);
          
          reconnectTimeoutsRef.current.set(jobId, timeoutId);
        } else {
          console.error(`❌ SSE max reconnection attempts reached for job ${jobId}`);
          const es = eventSourcesRef.current.get(jobId);
          if (es) {
            es.close();
            eventSourcesRef.current.delete(jobId);
          }
        }
      };
    };

    // Start initial connection
    connectSSE(0);

    // Return cleanup function
    return () => stopSSEMonitoring(jobId);
  }, []);

  const stopSSEMonitoring = useCallback((jobId) => {
    console.log(`🛑 Stopping SSE monitoring for job: ${jobId}`);
    
    // Close EventSource
    const eventSource = eventSourcesRef.current.get(jobId);
    if (eventSource) {
      eventSource.close();
      eventSourcesRef.current.delete(jobId);
    }

    // Clear reconnection timeout
    const reconnectTimeout = reconnectTimeoutsRef.current.get(jobId);
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
      reconnectTimeoutsRef.current.delete(jobId);
    }

    // Clear full progress timeout
    const fullProgressTimeout = fullProgressTimeoutsRef.current.get(jobId);
    if (fullProgressTimeout) {
      clearTimeout(fullProgressTimeout);
      fullProgressTimeoutsRef.current.delete(jobId);
    }

    // Remove callbacks
    callbacksRef.current.delete(jobId);

    // Update job state only if job still exists (avoid unnecessary updates)
    setActiveJobs(prev => {
      const job = prev.get(jobId);
      if (!job) {
        // Job already removed, no need to update
        return prev;
      }
      
      // Only update if connection status actually changed
      if (job.connectionStatus === 'disconnected') {
        // Already disconnected, no need to update
        return prev;
      }
      
      const updated = new Map(prev);
      updated.set(jobId, {
        ...job,
        connectionStatus: 'disconnected',
        disconnectedAt: new Date().toISOString()
      });
      return updated;
    });
  }, []);

  const getJobState = useCallback((jobId) => {
    return activeJobs.get(jobId);
  }, [activeJobs]);

  const getConnectionStatus = useCallback((jobId) => {
    const job = activeJobs.get(jobId);
    return {
      status: job?.connectionStatus || 'unknown',
      attempts: job?.connectionAttempts || 0,
      lastError: job?.lastError,
      isConnected: job?.connectionStatus === 'connected'
    };
  }, [activeJobs]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Close all EventSources
      eventSourcesRef.current.forEach((es) => es.close());
      eventSourcesRef.current.clear();
      
      // Clear all reconnection timeouts
      reconnectTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      reconnectTimeoutsRef.current.clear();
      
      // Clear all full progress timeouts
      fullProgressTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      fullProgressTimeoutsRef.current.clear();
      
      // Clear callbacks
      callbacksRef.current.clear();
    };
  }, []);

  return {
    startSSEMonitoring,
    stopSSEMonitoring,
    getJobState,
    getConnectionStatus,
    activeJobs
  };
};

export default useSSEJobMonitoring; 